/**
 * ============================================================================
 * RequalKanbanColumn — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: a single Kanban column rendering one cohort of requalification
 *          rows. Reused for PENDING / IN_PROGRESS / COMPLETED.
 *
 * A11Y: column is a <section> with aria-label so screen readers can
 *  distinguish the buckets even when the visual title is hidden under a
 *  narrow viewport.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './RequalificationDashboard.module.css';
import { RequalTaskCard } from './RequalTaskCard';
import type { RequalScheduleRow } from './useRequalificationScheduleQuery';
import type { RequalificationState } from '../EquipmentDetail/useEquipmentDetailQuery';

export interface RequalKanbanColumnProps {
  state: RequalificationState;
  title: string;
  rows: RequalScheduleRow[];
  count: number;
  onStart: (row: RequalScheduleRow) => void;
  onComplete: (row: RequalScheduleRow) => void;
}

export function RequalKanbanColumn({
  state,
  title,
  rows,
  count,
  onStart,
  onComplete,
}: RequalKanbanColumnProps) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const sorted = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          new Date(a.scheduled_for_utc).getTime() -
          new Date(b.scheduled_for_utc).getTime(),
      ),
    [rows],
  );

  return (
    <section
      className={styles.column}
      aria-label={(t as (k: string, o?: Record<string, unknown>) => string)(
        'equipment.requalDashboard.columnAria',
        {
          defaultValue: '{{title}} — {{count}} tasks',
          title,
          count: nf.format(count),
        },
      )}
    >
      <header className={styles.columnHeader}>
        <h2 className={styles.columnTitle}>{title}</h2>
        <span className={styles.columnBadge} aria-hidden="true">
          {nf.format(count)}
        </span>
      </header>
      {sorted.length === 0 ? (
        <p className={styles.columnEmpty}>
          {t('equipment.requalDashboard.columnEmpty', {
            defaultValue: 'No tasks here.',
          })}
        </p>
      ) : (
        <ul className={styles.cardList}>
          {sorted.map((row) => (
            <li key={row.requalification_id}>
              <RequalTaskCard
                row={row}
                onStart={onStart}
                onComplete={onComplete}
              />
            </li>
          ))}
        </ul>
      )}
      {/* state retained to keep aria semantics consistent across columns */}
      <span className="sr-only">{state}</span>
    </section>
  );
}
